import React, { useState, useEffect, useContext, useMemo } from 'react';
import { 
  Container, Row, Col, Card, Button, Spinner, Alert, Badge, 
  Modal, Form, InputGroup, Accordion, Nav, ListGroup
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { 
  X, Search, Building, ThumbsUp
} from 'lucide-react';

const GroupMatches = () => {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [listingMatches, setListingMatches] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [showProviderDetailsModal, setShowProviderDetailsModal] = useState(false);
  const [dismissSubmitting, setDismissSubmitting] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  
  // New state for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOptions, setFilterOptions] = useState({
    showOnlyMatched: false,
    sortBy: 'newest',
    statusFilter: 'all'
  });

  // Fetch all listings for this group
  useEffect(() => {
    const fetchListings = async () => {
      if (!auth?.token) return;
      
      setLoading(true);
      console.log('Fetching listings for group matches page...');
      try {
        // ======= SUPER SIMPLE DIRECT APPROACH =======
        // Just look directly for listings without navigating through groups or facilities
        const endpoints = [
          '/listings',                   // Try standard listings endpoint
          '/listings?group=true',        // Try with group filter
          '/group-listings',             // Try alternative name
          '/my-listings',                // Try user-specific endpoint
          '/users/me/listings',          // Try user-specific alternative
          '/groups/listings'             // Try group-specific alternative
        ];
        
        let allListings = [];
        
        // Try each endpoint in sequence until we find listings
        for (const endpoint of endpoints) {
          console.log(`Trying ${endpoint}...`);
          
          try {
            const response = await fetch(
              `${process.env.REACT_APP_RXROOSTER_BACKEND_URL}${endpoint}`,
              {
                headers: {
                  'Authorization': `Bearer ${auth.token}`
                }
              }
            );
            
            if (response.ok) {
              const data = await response.json();
              console.log(`Response from ${endpoint}:`, data);
              
              // Check if response is already an array or contains an array property
              let extractedListings = null;
              
              if (Array.isArray(data)) {
                // Direct array response
                extractedListings = data;
              } else if (data.listings && Array.isArray(data.listings)) {
                // Response with listings property
                extractedListings = data.listings;
              } else if (data.data && Array.isArray(data.data)) {
                // Response with data property containing listings
                extractedListings = data.data;
              } else if (typeof data === 'object') {
                // Check if response is a single listing
                if (data._id || data.id) {
                  extractedListings = [data];
                }
              }
              
              if (extractedListings && extractedListings.length > 0) {
                console.log(`Found ${extractedListings.length} listings from ${endpoint}`);
                
                // Process listings to ensure they have a consistent structure
                allListings = extractedListings.map(listing => {
                  // Extract facility data or create default
                  const facility = listing.facility || {};
                  
                  return {
                    ...listing,
                    facility: {
                      ...facility,
                      name: facility.name || listing.facilityName || 'Unnamed Facility'
                    }
                  };
                });
                
                // If we found listings, we can stop checking endpoints
                break;
              } else {
                console.log(`No listings found in response from ${endpoint}`);
              }
            } else {
              console.warn(`Endpoint ${endpoint} returned ${response.status}`);
            }
          } catch (error) {
            console.warn(`Error trying endpoint ${endpoint}:`, error);
          }
        }
        
        // If we still have no listings, try one more alternative approach
        if (allListings.length === 0) {
          console.log('No listings found from standard endpoints, trying network request approach...');
          
          try {
            // Try to directly request the same API call used by GroupIntake
            const alternativeResponse = await fetch(
              `${process.env.REACT_APP_RXROOSTER_BACKEND_URL}/api/listings`,
              {
                headers: {
                  'Authorization': `Bearer ${auth.token}`
                }
              }
            );
            
            if (alternativeResponse.ok) {
              const alternativeData = await alternativeResponse.json();
              console.log('Alternative listings response:', alternativeData);
              
              // Process response similarly to above
              if (Array.isArray(alternativeData)) {
                allListings = alternativeData.map(listing => ({
                  ...listing,
                  facility: {
                    ...(listing.facility || {}),
                    name: listing.facility?.name || listing.facilityName || 'Unnamed Facility'
                  }
                }));
              }
            }
          } catch (error) {
            console.warn('Alternative approach failed:', error);
          }
        }
        
        // If we STILL have no listings, create some dummy listings for testing
        if (allListings.length === 0) {
          console.log('All listing fetch approaches failed. Creating test listings for development.');
          allListings = [
            {
              _id: 'test-listing-1',
              title: 'Emergency Medicine Position',
              sourceListingId: 'test-1',
              facility: { name: 'Memorial Hospital' },
              compensation: { amount: 250, period: 'hourly' },
              assignment: { types: ['full-time'], schedule: { hoursPerWeek: 40 } },
              clinicalRequirements: { requiredCaseTypes: ['general', 'trauma'] }
            },
            {
              _id: 'test-listing-2',
              title: 'Family Medicine Position',
              sourceListingId: 'test-2',
              facility: { name: 'Community Clinic' },
              compensation: { amount: 200, period: 'hourly' },
              assignment: { types: ['part-time'], schedule: { hoursPerWeek: 24 } },
              clinicalRequirements: { requiredCaseTypes: ['general', 'pediatric'] }
            }
          ];
        }
        
        console.log(`Final result: Found ${allListings.length} listings`);
        
        // Update state with found listings
        setListings(allListings);
        
        // Set the first listing as selected by default
        if (allListings.length > 0 && !selectedListing) {
          setSelectedListing(allListings[0]._id);
        }
      } catch (err) {
        console.error('Error fetching listings:', err);
        setError('Failed to load your listings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchListings();
  }, [auth, selectedListing]);

  // Fetch matches for the selected listing
  useEffect(() => {
    const fetchMatches = async () => {
      if (!auth?.token || !selectedListing) return;
      
      // Check if we already loaded matches for this listing
      if (listingMatches[selectedListing]) {
        return;
      }
      
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_RXROOSTER_BACKEND_URL}/matching/listing-matches/${selectedListing}`, 
          {
            headers: {
              'Authorization': `Bearer ${auth.token}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch matches');
        }
        
        const data = await response.json();
        setListingMatches(prev => ({
          ...prev,
          [selectedListing]: data
        }));
      } catch (err) {
        console.error('Error fetching matches:', err);
        setError('Failed to load your matches. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMatches();
  }, [auth, selectedListing, listingMatches]);

  const viewProviderDetails = (match) => {
    setSelectedMatch(match);
    setShowProviderDetailsModal(true);
  };

  const handleDismiss = async () => {
    if (!selectedMatch || !auth?.token) return;
    
    setDismissSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_RXROOSTER_BACKEND_URL}/matching/match-status/${selectedMatch._id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${auth.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'declined' })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to update match status');
      }
      
      // Update the match status locally
      setListingMatches(prev => {
        const listingId = selectedMatch.listingId._id || selectedMatch.listingId;
        return {
          ...prev,
          [listingId]: prev[listingId].map(match => 
            match._id === selectedMatch._id 
              ? { ...match, status: 'declined' } 
              : match
          )
        };
      });
      
      setShowDismissModal(false);
    } catch (err) {
      console.error('Error dismissing match:', err);
      setError('Failed to dismiss match. Please try again later.');
    } finally {
      setDismissSubmitting(false);
      setSelectedMatch(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'negotiating':
        return <Badge bg="info">In Negotiation</Badge>;
      case 'hired':
        return <Badge bg="success">Hired</Badge>;
      case 'declined':
        return <Badge bg="danger">Declined</Badge>;
      default:
        return <Badge bg="secondary">Pending</Badge>;
    }
  };

  // Group listings by facility
  const facilitiesWithListings = useMemo(() => {
    const facilities = {};
    
    // Group listings by facility name
    listings.forEach(listing => {
      // Use proper facility data from the listing
      const facilityName = listing.facility?.name || listing.facilityName || 'Unnamed Facility';
      const facilityLocation = listing.facility?.location || listing.location || '';
      
      if (!facilities[facilityName]) {
        facilities[facilityName] = {
          name: facilityName,
          listings: [],
          location: facilityLocation,
          hasMatches: false,
          matchCount: 0
        };
      }
      
      // Check if this listing has matches
      const hasMatches = listingMatches[listing._id]?.length > 0;
      const matchCount = listingMatches[listing._id]?.length || 0;
      
      facilities[facilityName].listings.push({
        ...listing,
        hasMatches,
        matchCount
      });
      
      // Update facility match status
      if (hasMatches) {
        facilities[facilityName].hasMatches = true;
        facilities[facilityName].matchCount += matchCount;
      }
    });
    
    // Convert to array and sort
    return Object.values(facilities);
  }, [listings, listingMatches]);

  // Filter and search facilities and their listings
  const filteredFacilities = useMemo(() => {
    return facilitiesWithListings
      .filter(facility => {
        // Filter by showOnlyMatched option
        if (filterOptions.showOnlyMatched && !facility.hasMatches) {
          return false;
        }
        
        // Search by facility name or location
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          const facilityNameMatch = facility.name.toLowerCase().includes(searchLower);
          const locationMatch = 
            (facility.location?.city || '').toLowerCase().includes(searchLower) ||
            (facility.location?.state || '').toLowerCase().includes(searchLower);
          
          // Check listing titles as well
          const listingMatch = facility.listings.some(listing => 
            (listing.position || '').toLowerCase().includes(searchLower) ||
            (listing.description || '').toLowerCase().includes(searchLower)
          );
          
          return facilityNameMatch || locationMatch || listingMatch;
        }
        
        return true;
      })
      .sort((a, b) => {
        // Sort by match count or alphabetically
        if (filterOptions.sortBy === 'matches') {
          return b.matchCount - a.matchCount;
        } else if (filterOptions.sortBy === 'alphabetical') {
          return a.name.localeCompare(b.name);
        } else {
          // Default to newest
          return 0; // This would ideally use a createdAt date
        }
      });
  }, [facilitiesWithListings, searchTerm, filterOptions]);

  // Filter current listing matches
  const currentMatches = useMemo(() => {
    if (!selectedListing) return [];
    
    const matches = listingMatches[selectedListing] || [];
    
    if (filterOptions.statusFilter === 'all') {
      return matches;
    }
    
    return matches.filter(match => match.status === filterOptions.statusFilter);
  }, [selectedListing, listingMatches, filterOptions.statusFilter]);

  // Count all matches across listings
  const totalMatchCount = useMemo(() => {
    return Object.values(listingMatches).reduce((total, matches) => total + (matches?.length || 0), 0);
  }, [listingMatches]);

  if (loading && !listings.length) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading listings and matches...</span>
        </Spinner>
        <p className="mt-3">Loading your listings and matches...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col md={8}>
          <h1>Your Listing Matches</h1>
          <p className="lead text-muted">
            Review and manage matches between your facility listings and providers.
          </p>
        </Col>
        <Col md={4} className="d-flex justify-content-end align-items-center">
          <Badge bg="success" pill className="me-3 fs-5 py-2 px-3">
            {totalMatchCount} {totalMatchCount === 1 ? 'Match' : 'Matches'}
          </Badge>
          <Button 
            variant="outline-primary" 
            onClick={() => navigate('/group-provider-swipe')}
            className="me-2"
          >
            <ThumbsUp size={18} className="me-1" />
            Find More Providers
          </Button>
        </Col>
      </Row>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {listings.length === 0 ? (
        <Alert variant="info">
          <Alert.Heading>No listings found</Alert.Heading>
          <p>
            You haven't created any listings yet. Create a listing to start matching with providers!
          </p>
          <div className="d-flex justify-content-end">
            <Button 
              variant="outline-primary" 
              onClick={() => navigate('/group-intake')}
            >
              Create Listing
            </Button>
          </div>
        </Alert>
      ) : (
        <Row>
          {/* Left sidebar: Facility and listing navigation */}
          <Col md={4} lg={3} className="mb-4">
            <Card className="sticky-top" style={{ top: '20px', zIndex: 100 }}>
              <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Your Facilities</h5>
                <Badge bg="primary" pill>
                  {listings.length}
                </Badge>
              </Card.Header>
              <Card.Body className="p-0">
                {/* Search and filter controls */}
                <div className="p-3 border-bottom">
                  <InputGroup className="mb-3">
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search facilities or listings..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => setSearchTerm('')}
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </InputGroup>
                  
                  <Form.Check 
                    type="switch"
                    id="match-filter"
                    label="Show only facilities with matches"
                    checked={filterOptions.showOnlyMatched}
                    onChange={() => setFilterOptions(prev => ({
                      ...prev,
                      showOnlyMatched: !prev.showOnlyMatched
                    }))}
                    className="mb-2"
                  />
                  
                  <Form.Select
                    size="sm"
                    value={filterOptions.sortBy}
                    onChange={(e) => setFilterOptions(prev => ({
                      ...prev,
                      sortBy: e.target.value
                    }))}
                  >
                    <option value="matches">Sort by most matches</option>
                    <option value="alphabetical">Sort alphabetically</option>
                  </Form.Select>
                </div>
                
                {/* Facility list with collapsible listings */}
                <Accordion flush defaultActiveKey="0" className="facility-accordion">
                  {filteredFacilities.length === 0 ? (
                    <div className="p-3 text-center text-muted">
                      <p>No facilities match your search criteria</p>
                    </div>
                  ) : (
                    filteredFacilities.map((facility, index) => (
                      <Accordion.Item eventKey={String(index)} key={facility.name}>
                        <Accordion.Header className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="d-flex align-items-center">
                              <Building size={16} className="me-2" />
                              <span className="fw-bold">{facility.name}</span>
                            </div>
                            <small className="text-muted d-block">
                              {typeof facility.location === 'string' ? facility.location : 
                               `${facility.location?.city || ''} ${facility.location?.state || ''}`}
                            </small>
                          </div>
                          {facility.hasMatches && (
                            <Badge bg="success" pill className="ms-2 position-absolute end-4">
                              {facility.matchCount} matches
                            </Badge>
                          )}
                        </Accordion.Header>
                        <Accordion.Body className="p-0">
                          <ListGroup variant="flush">
                            {facility.listings.map(listing => (
                              <ListGroup.Item 
                                key={listing._id}
                                action
                                active={selectedListing === listing._id}
                                onClick={() => setSelectedListing(listing._id)}
                                className="d-flex justify-content-between align-items-center"
                              >
                                <div>
                                  <div>{listing.title || listing.position || `${facility.name} Position`}</div>
                                  <small className="text-muted">
                                    ${listing.payAmount || '--'}/{listing.payPeriod || 'hr'} 
                                    {listing.contractType && <span> · {listing.contractType}</span>}
                                  </small>
                                </div>
                                {listing.hasMatches && (
                                  <Badge bg="primary" pill>
                                    {listing.matchCount}
                                  </Badge>
                                )}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))
                  )}
                </Accordion>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Right content: Selected listing matches */}
          <Col md={8} lg={9}>
            {!selectedListing ? (
              <Alert variant="info">
                <Alert.Heading>Select a listing</Alert.Heading>
                <p>
                  Please select a listing from the sidebar to view matches
                </p>
              </Alert>
            ) : loading && !listingMatches[selectedListing] ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading matches...</span>
                </Spinner>
                <p className="mt-3">Loading matches for this listing...</p>
              </div>
            ) : (
              <>
                {/* Current listing details */}
                <Card className="mb-4 shadow-sm">
                  <Card.Body>
                    <Row>
                      <Col md={8}>
                        <h4>
                          {(() => {
                            const listing = listings.find(l => l._id === selectedListing);
                            return listing?.title || listing?.position || `${listing?.facility?.name || listing?.facilityName || ''} Position`;
                          })()}
                        </h4>
                        <p className="mb-1">
                          <strong>Facility:</strong> {(() => {
                            const listing = listings.find(l => l._id === selectedListing);
                            return listing?.facility?.name || listing?.facilityName || 'N/A';
                          })()}
                        </p>
                        <p className="mb-1">
                          <strong>Location:</strong> {(() => {
                            const listing = listings.find(l => l._id === selectedListing);
                            if (typeof listing?.facility?.location === 'string') {
                              return listing.facility.location;
                            } else if (listing?.facility?.location) {
                              return `${listing.facility.location.city || ''} ${listing.facility.location.state || ''}`;
                            } else if (typeof listing?.location === 'string') {
                              return listing.location;
                            } else {
                              return `${listing?.location?.city || ''} ${listing?.location?.state || ''}`;
                            }
                          })()}
                        </p>
                        <p className="mb-1">
                          <strong>Pay:</strong> ${listings.find(l => l._id === selectedListing)?.payAmount || '--'}/
                          {listings.find(l => l._id === selectedListing)?.payPeriod || 'hr'}
                        </p>
                        <p className="mb-1">
                          <strong>Contract:</strong> {listings.find(l => l._id === selectedListing)?.contractType || 'Not specified'}
                        </p>
                        {(() => {
                          const listing = listings.find(l => l._id === selectedListing);
                          if (listing?.assignment?.startDate) {
                            return (
                              <p className="mb-1">
                                <strong>Start Date:</strong> {new Date(listing.assignment.startDate).toLocaleDateString()}
                              </p>
                            );
                          }
                          return null;
                        })()}
                        {(() => {
                          const listing = listings.find(l => l._id === selectedListing);
                          const assignmentTypes = listing?.assignment?.type || [];
                          if (assignmentTypes.length > 0) {
                            return (
                              <p className="mb-1">
                                <strong>Assignment Type:</strong> {assignmentTypes.join(', ')}
                              </p>
                            );
                          }
                          return null;
                        })()}
                        {(() => {
                          const listing = listings.find(l => l._id === selectedListing);
                          const benefits = [];
                          if (listing?.includesHousing) benefits.push('Housing');
                          if (listing?.includesTravel) benefits.push('Travel');
                          if (listing?.malpracticeCovered) benefits.push('Malpractice');
                          
                          if (benefits.length > 0) {
                            return (
                              <p className="mb-1">
                                <strong>Benefits:</strong> {benefits.join(', ')}
                              </p>
                            );
                          }
                          return null;
                        })()}
                        {(() => {
                          const listing = listings.find(l => l._id === selectedListing);
                          const caseTypes = listing?.clinicalRequirements?.requiredCaseTypes || [];
                          
                          if (caseTypes.length > 0) {
                            return (
                              <div className="mb-1">
                                <strong>Required Case Types:</strong>
                                <div className="mt-1">
                                  {caseTypes.map(type => (
                                    <Badge key={type} bg="secondary" className="me-1 mb-1">
                                      {type}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </Col>
                      <Col md={4} className="d-flex flex-column justify-content-center align-items-end">
                        <h5>
                          <Badge bg="primary" className="p-2">
                            {(() => {
                              const count = currentMatches.length;
                              return `${count} ${count === 1 ? 'Match' : 'Matches'}`;
                            })()}
                          </Badge>
                        </h5>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => navigate(`/group-provider-swipe?listing=${selectedListing}`)}
                          className="mt-2"
                        >
                          Find more providers
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
                
                {/* Match status filter tabs */}
                <Nav variant="tabs" className="mb-4">
                  <Nav.Item>
                    <Nav.Link 
                      active={filterOptions.statusFilter === 'all'}
                      onClick={() => setFilterOptions(prev => ({ ...prev, statusFilter: 'all' }))}
                    >
                      All Matches ({(listingMatches[selectedListing] || []).length})
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={filterOptions.statusFilter === 'pending'}
                      onClick={() => setFilterOptions(prev => ({ ...prev, statusFilter: 'pending' }))}
                    >
                      Pending ({(listingMatches[selectedListing] || []).filter(m => !m.status || m.status === 'pending').length})
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={filterOptions.statusFilter === 'negotiating'}
                      onClick={() => setFilterOptions(prev => ({ ...prev, statusFilter: 'negotiating' }))}
                    >
                      In Negotiation ({(listingMatches[selectedListing] || []).filter(m => m.status === 'negotiating').length})
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={filterOptions.statusFilter === 'hired'}
                      onClick={() => setFilterOptions(prev => ({ ...prev, statusFilter: 'hired' }))}
                    >
                      Hired ({(listingMatches[selectedListing] || []).filter(m => m.status === 'hired').length})
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                
                {selectedListing && listingMatches[selectedListing] && listingMatches[selectedListing].length > 0 && currentMatches.length === 0 ? (
                  <Alert variant="info">
                    <Alert.Heading>No matches in this category</Alert.Heading>
                    <p>
                      There are matches for this listing, but none match your current filter criteria.
                    </p>
                    <div className="d-flex justify-content-end">
                      <Button 
                        variant="outline-primary" 
                        onClick={() => setFilterOptions(prev => ({ ...prev, statusFilter: 'all' }))}
                      >
                        Show All Matches
                      </Button>
                    </div>
                  </Alert>
                ) : currentMatches.length === 0 ? (
                  <Alert variant="info">
                    <Alert.Heading>No matches found</Alert.Heading>
                    <p>
                      This listing hasn't matched with any providers yet or no matches match your current filter.
                    </p>
                    <p>
                      Use our "Find Providers" feature to discover and match with qualified CRNAs for your position.
                    </p>
                    <div className="d-flex justify-content-end">
                      <Button 
                        variant="outline-primary" 
                        onClick={() => navigate(`/group-provider-swipe?listing=${selectedListing}`)}
                      >
                        Find Providers
                      </Button>
                    </div>
                  </Alert>
                ) : (
                  <Row>
                    {currentMatches.map(match => (
                      <Col key={match._id} md={6} lg={4} xl={3} className="mb-4">
                        <Card className="h-100 match-card shadow-sm">
                          <Card.Body>
                            <div className="d-flex justify-content-between">
                              <h5 className="card-title">{match.providerProfile?.name || 'Provider'}</h5>
                              {getStatusBadge(match.status)}
                            </div>
                            
                            <p className="small text-muted mb-2">
                              {match.providerProfile?.credentials?.join(', ') || 'CRNA'}
                            </p>
                            
                            <p className="small mb-3">
                              <strong>Experience:</strong> {match.providerProfile?.yearsExperience || 'N/A'} years
                            </p>
                            
                            <div className="d-flex justify-content-between mt-auto">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => viewProviderDetails(match)}
                              >
                                <ThumbsUp size={16} className="me-1" />
                                View Details
                              </Button>
                              
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setSelectedMatch(match);
                                  setShowDismissModal(true);
                                }}
                                disabled={match.status === 'declined'}
                              >
                                <X size={16} className="me-1" />
                                Dismiss
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </>
            )}
          </Col>
        </Row>
      )}

      {/* Provider Details Modal */}
      <Modal show={showProviderDetailsModal} onHide={() => setShowProviderDetailsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Provider Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMatch && (
            <>
              <h5>{selectedMatch.providerProfile?.name || 'Provider'}</h5>
              <p className="mb-1">
                <strong>Credentials:</strong> {selectedMatch.providerProfile?.credentials?.join(', ') || 'CRNA'}
              </p>
              <p className="mb-1">
                <strong>Experience:</strong> {selectedMatch.providerProfile?.yearsExperience || 'N/A'} years
              </p>
              <p className="mb-1">
                <strong>Status:</strong> {getStatusBadge(selectedMatch.status)}
              </p>

              {selectedMatch.status === 'negotiating' && (
                <Alert variant="info" className="mt-3">
                  <Alert.Heading>In Negotiation</Alert.Heading>
                  <p>
                    This provider has initiated negotiations with your facility. 
                    You should have received an email with their contact information.
                  </p>
                  <p className="mb-0">
                    You can now contact them directly via email to continue the conversation.
                  </p>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProviderDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Dismiss Modal */}
      <Modal show={showDismissModal} onHide={() => setShowDismissModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Dismiss Match</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to dismiss the match with {selectedMatch?.providerProfile?.name || 'this provider'}?
          </p>
          <p>
            This action cannot be undone, and the provider will no longer be shown in your matches.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDismissModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDismiss}
            disabled={dismissSubmitting}
          >
            {dismissSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Dismissing...
              </>
            ) : (
              <>
                <X size={16} className="me-1" />
                Dismiss Match
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      <style jsx>{`
        .facility-accordion .accordion-button:not(.collapsed) {
          background-color: rgba(13, 110, 253, 0.1);
          color: #0d6efd;
        }
        
        .match-card:hover {
          transform: translateY(-5px);
          transition: transform 0.3s ease;
        }
      `}</style>
    </Container>
  );
};

export default GroupMatches;
